import {useCallback, useEffect, useMemo, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {collection, doc, limit, onSnapshot, orderBy, query} from "firebase/firestore";
import firebase from "../../core/firebase";
import {Shell} from "../../common/shell/shell.page";
import {PageHeader} from "../../common/components/page-header";
import {LoadingOverlay} from "../../common/components/loading-overlay";
import {Permission} from "../../users/user.entity";
import application from "../../core/application";
import {AiCreditsSection} from "../components/ai-credits-section";
import {PlanModulesSection} from "../components/plan-modules-section";
import {StripePaymentDialog} from "../components/stripe-payment-dialog";
import {moduleBillingService, type BillingModuleId, type ModuleBundleQuote, type PlanOverview} from "../module-billing.service";
import {FULL_BUNDLE_AI_CREDIT_BONUS, MODULE_GROUPS} from "../feature-module-map";

const formatMoney = (value: number) => `R$ ${value.toFixed(2).replace(".", ",")}`;

const formatDate = (value: number | null) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "A definir";

const formatDateTime = (value: number | null) =>
  value ? new Date(value).toLocaleString("pt-BR") : "A definir";

const cardLabel = (card: PlanOverview["card"]) => {
  if (!card) return "Nenhum cartão configurado";
  return `${card.brand.toUpperCase()} final ${card.last4} · ${String(card.expMonth).padStart(2, "0")}/${card.expYear}`;
};

const getModuleLabel = (moduleId: string) => {
  for (const group of MODULE_GROUPS) {
    const variant = group.variants.find((item) => item.id === moduleId);
    if (variant) return `${group.name} · ${variant.name}`;
  }
  return moduleId;
};

type AiCreditTransaction = {
  id: string;
  kind: "usage" | "purchase" | "bonus" | "adjustment";
  description: string;
  direction: "credit" | "debit";
  creditsDelta: number;
  createdAt: number;
};

export const MyPlanPage = () => {
  application.useHasPermission(Permission.ADMIN);
  const theme = useTheme();
  const [overview, setOverview] = useState<PlanOverview | null>(null);
  const [aiCredits, setAiCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cardDialog, setCardDialog] = useState<{clientSecret: string; setupIntentId: string} | null>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [fullBundleQuote, setFullBundleQuote] = useState<ModuleBundleQuote | null>(null);
  const [fullBundleLoading, setFullBundleLoading] = useState(false);
  const [fullBundlePayment, setFullBundlePayment] = useState<{clientSecret: string; amountBrl: number} | null>(null);
  const [pendingPlanPaymentDialog, setPendingPlanPaymentDialog] = useState<{clientSecret: string; amountBrl: number} | null>(null);
  const [pendingAiPaymentDialog, setPendingAiPaymentDialog] = useState<{clientSecret: string; amountBrl: number; credits: number} | null>(null);
  const [cancelPendingLoading, setCancelPendingLoading] = useState<null | "plan" | "ai_credits">(null);
  const [aiCreditHistory, setAiCreditHistory] = useState<AiCreditTransaction[]>([]);
  const [toast, setToast] = useState<{open: boolean; severity: "success" | "error"; message: string}>({
    open: false,
    severity: "success",
    message: "",
  });

  const fullBundleTargetIds = useMemo(
    () =>
      MODULE_GROUPS.map((group) =>
        [...group.variants].sort((a, b) => b.monthlyPrice - a.monthlyPrice)[0].id
      ),
    []
  );

  const getCurrentModuleForGroup = useCallback((groupId: string): BillingModuleId | null => {
    if (!overview?.modules) return null;
    const group = MODULE_GROUPS.find((item) => item.id === groupId);
    if (!group) return null;
    const active = group.variants.filter((variant) => {
      const status = overview.modules[variant.id]?.status;
      return status === "active" || status === "cancel_at_period_end" || status === "pending_payment";
    });
    if (active.length === 0) return null;
    return [...active].sort((a, b) => {
      const aInStripe = overview.modules[a.id]?.inStripeSubscription ? 1 : 0;
      const bInStripe = overview.modules[b.id]?.inStripeSubscription ? 1 : 0;
      if (aInStripe !== bInStripe) return bInStripe - aInStripe;
      return b.monthlyPrice - a.monthlyPrice;
    })[0].id;
  }, [overview?.modules]);

  const fullBundleDiffs = useMemo(() => {
    return MODULE_GROUPS.map((group) => {
      const target = [...group.variants].sort((a, b) => b.monthlyPrice - a.monthlyPrice)[0];
      const currentId = getCurrentModuleForGroup(group.id);
      const current = currentId ? group.variants.find((item) => item.id === currentId) || null : null;
      return {
        groupId: group.id,
        groupName: group.name,
        current,
        target,
      };
    }).filter((diff) => diff.current?.id !== diff.target.id);
  }, [getCurrentModuleForGroup]);

  const shouldShowFullBundleBanner = fullBundleDiffs.length > 0;
  const hasPendingPlanPayment = Boolean(overview?.pendingPlanPayment);
  const hasPendingAiPayment = Boolean(overview?.pendingAiCreditsPayment);
  const pendingPlanPayment = overview?.pendingPlanPayment || null;
  const pendingAiCreditsPayment = overview?.pendingAiCreditsPayment || null;
  const planActionsLocked = hasPendingPlanPayment;
  const aiActionsLocked = hasPendingAiPayment;

  const loadOverview = useCallback(async () => {
    setError(null);
    try {
      const data = await moduleBillingService.stripeGetPlanOverview();
      setOverview(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar seu plano.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFullBundleQuote = useCallback(async () => {
    setFullBundleLoading(true);
    try {
      const quote = await moduleBillingService.quoteBundle(fullBundleTargetIds);
      setFullBundleQuote(quote);
    } catch {
      setFullBundleQuote(null);
    } finally {
      setFullBundleLoading(false);
    }
  }, [fullBundleTargetIds]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    const user = application.user.get();
    if (!user) return;
    const uid = (user as {id: string}).id;
    const subRef = doc(firebase.db, `customerSubscriptions/${uid}`);
    const unsubscribe = onSnapshot(subRef, (snap) => {
      if (snap.exists()) {
        setAiCredits((snap.data()?.aiCredits as number) ?? 0);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const user = application.user.get();
    if (!user) return;
    const uid = (user as {id: string}).id;
    const historyRef = collection(firebase.db, `customerSubscriptions/${uid}/aiCreditTransactions`);
    const historyQuery = query(historyRef, orderBy("createdAt", "desc"), limit(30));
    const unsubscribe = onSnapshot(historyQuery, (snap) => {
      const rows = snap.docs.map((item) => {
        const data = item.data() as Partial<AiCreditTransaction>;
        return {
          id: item.id,
          kind: (data.kind as AiCreditTransaction["kind"]) || "adjustment",
          description: data.description || "Movimentação de créditos IA",
          direction: (data.direction as AiCreditTransaction["direction"]) || "debit",
          creditsDelta: typeof data.creditsDelta === "number" ? data.creditsDelta : 0,
          createdAt: typeof data.createdAt === "number" ? data.createdAt : 0,
        };
      });
      setAiCreditHistory(rows);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!shouldShowFullBundleBanner) {
      setFullBundleQuote(null);
      return;
    }
    void loadFullBundleQuote();
  }, [loadFullBundleQuote, shouldShowFullBundleBanner]);

  const changeCard = useCallback(async () => {
    setCardLoading(true);
    setError(null);
    try {
      const setup = await moduleBillingService.stripeCreateSetupIntent();
      setCardDialog(setup);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível iniciar a troca de cartão.");
    } finally {
      setCardLoading(false);
    }
  }, []);

  const saveCard = useCallback(
    async (setupIntentId?: string) => {
      const intentId = setupIntentId || cardDialog?.setupIntentId;
      if (!intentId) return;
      setToast({open: true, severity: "success", message: "Salvando cartão…"});
      try {
        await moduleBillingService.stripeSaveSetupIntentPaymentMethod(intentId);
        await loadOverview();
        setToast({open: true, severity: "success", message: "Cartão atualizado com sucesso."});
      } catch (e) {
        setToast({
          open: true,
          severity: "error",
          message: e instanceof Error ? e.message : "Não foi possível salvar o cartão.",
        });
      } finally {
        setCardDialog(null);
      }
    },
    [cardDialog?.setupIntentId, loadOverview]
  );

  const cancelSubscription = useCallback(async () => {
    const confirmed = window.confirm(
      "Cancelar toda a assinatura no fim do ciclo atual? Os módulos pagos continuam ativos até a data de encerramento."
    );
    if (!confirmed) return;

    setCancelLoading(true);
    setError(null);
    try {
      await moduleBillingService.stripeCancelSubscriptionAtPeriodEnd();
      await loadOverview();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível cancelar a assinatura.");
    } finally {
      setCancelLoading(false);
    }
  }, [loadOverview]);

  const undoCancelSubscription = useCallback(async () => {
    const confirmed = window.confirm(
      "Deseja remover o cancelamento agendado e manter sua assinatura ativa?"
    );
    if (!confirmed) return;

    setCancelLoading(true);
    setError(null);
    try {
      await moduleBillingService.stripeUndoSubscriptionCancelAtPeriodEnd();
      await loadOverview();
      setToast({ open: true, severity: "success", message: "Cancelamento desfeito. Assinatura reativada." });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível desfazer o cancelamento.");
    } finally {
      setCancelLoading(false);
    }
  }, [loadOverview]);

  const activateFullBundle = useCallback(async () => {
    if (planActionsLocked) return;
    setError(null);
    try {
      const checkout = await moduleBillingService.stripeCreateModuleBundleCheckout(fullBundleTargetIds);
      if (checkout.status === "pending") {
        setToast({
          open: true,
          severity: "success",
          message: "A cobrança anterior ainda está processando. O pacote completo será aplicado após confirmação.",
        });
        return;
      }
      if (checkout.status === "scheduled") {
        setToast({
          open: true,
          severity: "success",
          message: "Pacote completo agendado para o próximo ciclo.",
        });
        await loadOverview();
        return;
      }
      if (checkout.requiresPayment && checkout.clientSecret && (fullBundleQuote?.amountDueNowTotal || 0) > 0) {
        setFullBundlePayment({
          clientSecret: checkout.clientSecret,
          amountBrl: fullBundleQuote?.amountDueNowTotal || 0,
        });
        return;
      }
      setToast({
        open: true,
        severity: "success",
        message:
          (fullBundleQuote?.amountDueNowTotal || 0) > 0 ?
            `Pacote completo em processamento. Após a confirmação do pagamento, você pode receber até ${FULL_BUNDLE_AI_CREDIT_BONUS.toLocaleString("pt-BR")} créditos de IA de bônus (pacote completo no período atual).` :
            `Pacote completo ativado. Até ${FULL_BUNDLE_AI_CREDIT_BONUS.toLocaleString("pt-BR")} créditos de IA de bônus podem ser creditados quando todos os módulos pagos estiverem ativos neste ciclo.`,
      });
      await loadOverview();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível ativar o pacote completo.");
    }
  }, [fullBundleQuote?.amountDueNowTotal, fullBundleTargetIds, loadOverview, planActionsLocked]);

  const cancelPendingPayment = useCallback(async (kind: "plan" | "ai_credits", targetId: string) => {
    const confirmed = window.confirm("Deseja cancelar esta cobrança pendente?");
    if (!confirmed) return;
    setCancelPendingLoading(kind);
    setError(null);
    try {
      await moduleBillingService.stripeCancelPendingPayment(kind, targetId);
      await loadOverview();
      setToast({
        open: true,
        severity: "success",
        message: kind === "plan" ? "Cobrança pendente do plano cancelada." : "Cobrança pendente de créditos de IA cancelada.",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível cancelar a cobrança pendente.");
    } finally {
      setCancelPendingLoading(null);
    }
  }, [loadOverview]);

  if (loading) {
    return (
      <Shell title="Meu Plano">
        <LoadingOverlay message="Carregando plano..." />
      </Shell>
    );
  }

  return (
    <Shell title="Meu Plano">
      <PageHeader
        title="Meu Plano"
        description="Acompanhe assinatura, módulos contratados, cartão configurado e créditos de IA."
      />

      {error ? <Alert severity="error" sx={{mb: 2}}>{error}</Alert> : null}

      <Stack spacing={3}>
        {pendingPlanPayment ? (
          <Alert
            severity={pendingPlanPayment.isProcessing ? "info" : "warning"}
            icon={pendingPlanPayment.isProcessing ? <AutorenewIcon /> : <PendingActionsIcon />}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={800}>
                Pendência de pagamento do plano: {formatMoney(pendingPlanPayment.amountDue)}
              </Typography>
              <Typography variant="body2">
                Status: <strong>{pendingPlanPayment.status}</strong> · criado em{" "}
                <strong>{formatDate(pendingPlanPayment.createdAt)}</strong>.
              </Typography>
              <Typography variant="body2">
                Enquanto essa cobrança estiver pendente, alterações de plano ficam bloqueadas.
              </Typography>
              <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!pendingPlanPayment.canContinue}
                  onClick={async () => {
                    try {
                      if (pendingPlanPayment.clientSecret) {
                        setPendingPlanPaymentDialog({
                          clientSecret: pendingPlanPayment.clientSecret,
                          amountBrl: pendingPlanPayment.amountDue,
                        });
                        return;
                      }
                      if (pendingPlanPayment.hostedInvoiceUrl) {
                        window.open(pendingPlanPayment.hostedInvoiceUrl, "_blank", "noopener,noreferrer");
                        return;
                      }
                      const continued = await moduleBillingService.stripeContinuePendingPlanPayment(
                        pendingPlanPayment.paymentIntentId || undefined
                      );
                      if (continued.settled) {
                        await loadOverview();
                        setToast({
                          open: true,
                          severity: "success",
                          message: "Cobramos automaticamente no cartão salvo e o plano foi atualizado.",
                        });
                        return;
                      }
                      if (!continued.clientSecret) {
                        throw new Error("Não foi possível iniciar o pagamento.");
                      }
                      setPendingPlanPaymentDialog({
                        clientSecret: continued.clientSecret,
                        amountBrl: continued.amountBrl || pendingPlanPayment.amountDue,
                      });
                    } catch (e) {
                      setError(e instanceof Error ? e.message : "Não foi possível continuar o pagamento pendente.");
                    }
                  }}
                >
                  {pendingPlanPayment.isProcessing ? "Processando pagamento" : "Continuar pagamento"}
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={!pendingPlanPayment.canCancel || cancelPendingLoading === "plan"}
                  onClick={() => void cancelPendingPayment("plan", pendingPlanPayment.targetId)}
                >
                  Cancelar contratação
                </Button>
              </Stack>
              {pendingPlanPayment.moduleChanges.length > 0 ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={700}>Mudanças pendentes:</Typography>
                  {pendingPlanPayment.moduleChanges.map((change) => (
                    <Typography key={`${change.family}-${change.toModuleId}`} variant="body2">
                      - {change.fromModuleId ? `${getModuleLabel(change.fromModuleId)} → ` : "Ativar "} {getModuleLabel(change.toModuleId)}
                    </Typography>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Alert>
        ) : null}

        {pendingAiCreditsPayment ? (
          <Alert
            severity={pendingAiCreditsPayment.isProcessing ? "info" : "warning"}
            icon={pendingAiCreditsPayment.isProcessing ? <AutorenewIcon /> : <PendingActionsIcon />}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={800}>
                Fatura pendente de créditos IA: {formatMoney(pendingAiCreditsPayment.amountDue)}
              </Typography>
              <Typography variant="body2">
                {pendingAiCreditsPayment.credits > 0 ?
                  `${pendingAiCreditsPayment.credits.toLocaleString("pt-BR")} créditos serão liberados após confirmação. ` :
                  ""}
                Status: <strong>{pendingAiCreditsPayment.status}</strong> · criada em{" "}
                <strong>{formatDate(pendingAiCreditsPayment.createdAt)}</strong>.
              </Typography>
              <Typography variant="body2">
                Compra de créditos fica bloqueada até finalizar ou cancelar esta pendência. O plano continua liberado.
              </Typography>
              <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!pendingAiCreditsPayment.canContinue}
                  onClick={() => {
                    if (!pendingAiCreditsPayment.clientSecret) return;
                    setPendingAiPaymentDialog({
                      clientSecret: pendingAiCreditsPayment.clientSecret,
                      amountBrl: pendingAiCreditsPayment.amountDue,
                      credits: pendingAiCreditsPayment.credits,
                    });
                  }}
                >
                  {pendingAiCreditsPayment.isProcessing ? "Processando pagamento" : "Continuar pagamento"}
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={!pendingAiCreditsPayment.canCancel || cancelPendingLoading === "ai_credits"}
                  onClick={() => void cancelPendingPayment("ai_credits", pendingAiCreditsPayment.targetId)}
                >
                  Cancelar fatura IA
                </Button>
              </Stack>
            </Stack>
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{
              height: "100%",
              background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
            }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.primary.main, 0.15), color: "primary.main" }}>
                      <PaymentsIcon />
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary" fontWeight={800} sx={{ letterSpacing: 0.5, lineHeight: 1.2 }}>
                        Plano e Cobrança
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ciclo: {formatDate(overview?.currentPeriodStart || null)} até {formatDate(overview?.currentPeriodEnd || null)}
                      </Typography>
                    </Box>
                  </Stack>
                  
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="h5" fontWeight={800}>
                      {overview?.subscriptionCancelAtPeriodEnd ? "Cancelamento agendado" : "Ativo"}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        overview?.subscriptionCancelAtPeriodEnd ?
                          `Cancela em ${formatDate(overview.subscriptionAccessEndsAt)}` :
                          overview?.status || "inactive"
                      }
                      color={overview?.status === "active" && !overview?.subscriptionCancelAtPeriodEnd ? "success" : "default"}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1, borderColor: alpha(theme.palette.primary.main, 0.1) }} />

                  {!overview?.subscriptionCancelAtPeriodEnd ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>PRÓXIMA COBRANÇA</Typography>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="h5" fontWeight={800}>{formatMoney(overview?.nextChargeAmount || 0)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          em <strong>{formatDate(overview?.nextChargeAt || null)}</strong>
                        </Typography>
                      </Stack>
                    </Box>
                  ) : (
                    <Alert severity="info" variant="outlined" sx={{ py: 0.5, borderRadius: 2, mt: 1 }}>
                      Assinatura com cancelamento agendado para <strong>{formatDate(overview?.subscriptionAccessEndsAt || null)}</strong>.
                    </Alert>
                  )}

                  {!overview?.subscriptionCancelAtPeriodEnd && (overview?.nextChargeDiscountAmount || 0) > 0 ? (
                    <Alert severity="success" variant="outlined" sx={{ py: 0.5, borderRadius: 2, mt: 1 }}>
                      Desconto aplicado: <strong>{formatMoney(overview?.nextChargeDiscountAmount || 0)}</strong>
                      {overview?.nextChargeSubtotal ? (
                        <> sobre subtotal de {formatMoney(overview.nextChargeSubtotal)}.</>
                      ) : null}
                    </Alert>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={0} sx={{
              height: "100%",
              border: 1, borderColor: "divider"
            }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.text.primary, 0.05), color: "text.secondary" }}>
                    <CreditCardIcon />
                  </Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={800} sx={{ letterSpacing: 0.5 }}>
                    Cartão configurado
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>{cardLabel(overview?.card || null)}</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    disabled={cardLoading || planActionsLocked}
                    onClick={() => void changeCard()}
                    sx={{ alignSelf: "flex-start", borderRadius: 2, fontWeight: 700 }}
                  >
                    {cardLoading ? <CircularProgress size={18} color="inherit" /> : "Mudar cartão"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={0} sx={{
              height: "100%",
              background: `linear-gradient(145deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
            }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.info.main, 0.15), color: "info.main" }}>
                    <AutoAwesomeIcon />
                  </Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={800} sx={{ letterSpacing: 0.5 }}>
                    Créditos de IA
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {aiCredits === null ? "..." : aiCredits.toLocaleString("pt-BR")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uso em automações
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    disabled={aiActionsLocked}
                    onClick={() => {
                      document.getElementById("ai-credits-section")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    sx={{ alignSelf: "flex-start", borderRadius: 2, fontWeight: 700 }}
                  >
                    Adicionar crédito
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {shouldShowFullBundleBanner ? (
          <Paper
            elevation={0}
            sx={{
              overflow: "hidden",
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.22),
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.12
              )} 0%, ${alpha(theme.palette.primary.dark, 0.06)} 55%, ${alpha(
                theme.palette.background.paper,
                1
              )} 100%)`,
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2.2}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <RocketLaunchOutlinedIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Pacote completo Symplou
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Faltam {fullBundleDiffs.length} ajuste(s) para liberar tudo no nível máximo.
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    icon={<AutoAwesomeIcon />}
                    label={`Brinde: ${formatMoney(fullBundleQuote?.finalMonthlyTotal || 0)} em créditos IA`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                  />
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {fullBundleDiffs.slice(0, 4).map((diff) => (
                    <Chip
                      key={diff.groupId}
                      size="small"
                      label={`${diff.groupName}: ${diff.current ? `${diff.current.name} → ` : ""}${diff.target.name}`}
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                  {fullBundleDiffs.length > 4 ? (
                    <Chip
                      size="small"
                      label={`+${fullBundleDiffs.length - 4} ajustes`}
                    />
                  ) : null}
                </Stack>

                {fullBundleLoading ? (
                  <Alert severity="info" variant="outlined">Calculando valores do pacote completo...</Alert>
                ) : fullBundleQuote ? (
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">Pacote por mês</Typography>
                            <Typography fontWeight={800}>{formatMoney(fullBundleQuote.finalMonthlyTotal)}</Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">Desconto</Typography>
                            <Typography fontWeight={800} color="primary.main">
                              <LocalOfferOutlinedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-bottom" }} />
                              {fullBundleQuote.discountPct}%
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">Cobrança imediata</Typography>
                            <Typography fontWeight={800}>{formatMoney(fullBundleQuote.amountDueNowTotal)}</Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="warning" variant="outlined">Não foi possível calcular o pacote completo agora.</Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
                  <Button
                    variant="contained"
                    onClick={() => void activateFullBundle()}
                    disabled={planActionsLocked || fullBundleLoading || !fullBundleQuote}
                    sx={{ fontWeight: 700, px: 2.5 }}
                  >
                    Ativar pacote completo
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    O plano atual continua ativo até confirmar a alteração.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        ) : null}

        <PlanModulesSection
          onChanged={loadOverview}
          disabled={planActionsLocked}
          disabledReason="Existe uma cobrança pendente do plano. Finalize ou cancele essa cobrança para alterar módulos."
        />

        <Box id="ai-credits-section" sx={{ scrollMarginTop: 100 }}>
          <Card
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <AiCreditsSection disablePurchase={aiActionsLocked} />
            </CardContent>
          </Card>
        </Box>

        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Histórico de créditos IA</Typography>
            {aiCreditHistory.length ? (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {aiCreditHistory.map((tx) => (
                  <Stack
                    key={tx.id}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                    sx={{
                      py: 1.5,
                      "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.4), borderRadius: 2, px: 1, mx: -1 },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>{tx.description}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(tx.createdAt)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${tx.direction === "credit" ? "+" : "-"}${Math.abs(tx.creditsDelta).toLocaleString("pt-BR")} créditos`}
                      color={tx.direction === "credit" ? "success" : "default"}
                      variant={tx.direction === "credit" ? "filled" : "outlined"}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                Nenhuma movimentação de créditos IA encontrada ainda.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Histórico de pagamentos</Typography>
            {overview?.paymentHistory?.length ? (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {overview.paymentHistory.map((invoice) => (
                  <Stack
                    key={invoice.id}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                    sx={{
                      py: 1.5,
                      "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.4), borderRadius: 2, px: 1, mx: -1 }
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>
                        {formatMoney(typeof invoice.displayAmount === "number" ? invoice.displayAmount : invoice.amountPaid || invoice.amountDue)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(invoice.paidAt || invoice.createdAt)} · <span style={{ textTransform: "capitalize" }}>{invoice.status || "sem status"}</span>
                      </Typography>
                    </Box>
                    {invoice.hostedInvoiceUrl ? (
                      <Link href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer" underline="none">
                        <Button size="small" variant="text" sx={{ fontWeight: 700 }}>
                          Ver recibo
                        </Button>
                      </Link>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                Nenhum pagamento encontrado ainda.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: 1, borderColor: alpha(theme.palette.error.main, 0.3) }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
              <Box>
                <Typography variant="h6" fontWeight={800} color="error.main">Cancelar assinatura</Typography>
                <Typography variant="body2" color="text.secondary">
                  Cancela todos os módulos pagos no fim do ciclo atual. O acesso continua ativo até <strong>{formatDate(overview?.currentPeriodEnd || null)}</strong>.
                </Typography>
              </Box>
              <Button
                color="error"
                variant={overview?.subscriptionCancelAtPeriodEnd ? "contained" : "outlined"}
                startIcon={cancelLoading ? <CircularProgress size={16} color="error" /> : <CancelOutlinedIcon />}
                disabled={planActionsLocked || cancelLoading || !overview?.stripeSubscriptionId}
                onClick={() => {
                  if (overview?.subscriptionCancelAtPeriodEnd) {
                    void undoCancelSubscription();
                    return;
                  }
                  void cancelSubscription();
                }}
                sx={{
                  flexShrink: 0,
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              >
                {overview?.subscriptionCancelAtPeriodEnd ? "Desfazer cancelamento" : "Cancelar assinatura"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <StripePaymentDialog
        open={Boolean(cardDialog)}
        clientSecret={cardDialog?.clientSecret || ""}
        amountBrl={0}
        title="Mudar cartão"
        description="Cadastre o novo cartão que será usado nas próximas cobranças da assinatura."
        onSuccess={(setupIntentId) => void saveCard(setupIntentId)}
        onClose={() => setCardDialog(null)}
      />
      <StripePaymentDialog
        open={Boolean(fullBundlePayment)}
        clientSecret={fullBundlePayment?.clientSecret || ""}
        amountBrl={fullBundlePayment?.amountBrl || 0}
        expectedModuleIds={fullBundleTargetIds}
        title="Ativar pacote completo"
        description="Finalize o pagamento para ativar o pacote completo de módulos."
        onSuccess={async () => {
          setToast({
            open: true,
            severity: "success",
            message: "Pagamento confirmado! Atualizando seu plano…",
          });
          try {
            await loadOverview();
            setToast({
              open: true,
              severity: "success",
              message: "Pacote completo atualizado. Créditos de IA de bônus podem levar alguns instantes para aparecer no saldo.",
            });
          } finally {
            setFullBundlePayment(null);
          }
        }}
        onClose={() => setFullBundlePayment(null)}
      />
      <StripePaymentDialog
        open={Boolean(pendingPlanPaymentDialog)}
        clientSecret={pendingPlanPaymentDialog?.clientSecret || ""}
        amountBrl={pendingPlanPaymentDialog?.amountBrl || 0}
        title="Continuar pagamento do plano"
        description="Finalize este pagamento pendente para desbloquear alterações do seu plano."
        onSuccess={async () => {
          setToast({open: true, severity: "success", message: "Pagamento confirmado! Atualizando seu plano…"});
          try {
            await loadOverview();
            setToast({open: true, severity: "success", message: "Plano atualizado com sucesso."});
          } finally {
            setPendingPlanPaymentDialog(null);
          }
        }}
        onClose={() => setPendingPlanPaymentDialog(null)}
      />
      <StripePaymentDialog
        open={Boolean(pendingAiPaymentDialog)}
        clientSecret={pendingAiPaymentDialog?.clientSecret || ""}
        amountBrl={pendingAiPaymentDialog?.amountBrl || 0}
        title="Continuar pagamento de créditos IA"
        description={`Finalize este pagamento para liberar ${pendingAiPaymentDialog?.credits.toLocaleString("pt-BR") || 0} créditos.`}
        onSuccess={async () => {
          setToast({open: true, severity: "success", message: "Pagamento confirmado! Liberando créditos…"});
          try {
            await loadOverview();
            setToast({open: true, severity: "success", message: "Créditos de IA atualizados."});
          } finally {
            setPendingAiPaymentDialog(null);
          }
        }}
        onClose={() => setPendingAiPaymentDialog(null)}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((current) => ({...current, open: false}))}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert
          onClose={() => setToast((current) => ({...current, open: false}))}
          severity={toast.severity}
          variant="filled"
          sx={{width: "100%"}}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Shell>
  );
};
